import { useState } from 'react';
import clsx from 'clsx';
import { t } from '@lingui/core/macro';
import type { Note } from '../../types/api';
import ButtonIcon from '../ui/ButtonIcon';
import Modal from '../ui/Modal';

interface NoteDrawableProps {
  note: Pick<Note, 'id' | 'highlight_id' | 'drawable'>;
  className?: string;
}

/**
 * Saved drawings are the SVG from ink.render_svg: one file the size of the
 * authoring canvas (the format example is 800×240), with round pen polylines,
 * a translucent highlighter stroke, and a dark-mode recolor for near-black ink.
 */

/** The canvas width/height attributes are large; keep the image inside its column. */
const imageClassName =
  'note-drawable border-default block h-auto w-auto max-w-full min-w-0 rounded-sm border object-contain';

function drawableSrc(highlightId: string, contentHash: string) {
  return `/data/highlight/${encodeURIComponent(highlightId)}/drawable.svg?v=${encodeURIComponent(contentHash)}`;
}

function NoteDrawable({ note, className }: NoteDrawableProps) {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const drawable = note.drawable;
  if (!drawable) {
    return null;
  }

  const highlightId = String(note.highlight_id ?? note.id);
  const src = drawableSrc(highlightId, drawable.content_hash);

  if (failedSrc === src) {
    return null;
  }

  const alt = t`Handwritten note`;

  return (
    <>
      <div className={clsx('relative inline-block max-w-full', className)}>
        <img
          src={src}
          alt={alt}
          className={imageClassName}
          onError={() => setFailedSrc(src)}
        />
        <ButtonIcon
          icon="expand"
          title={t`Full width`}
          size="sm"
          className="bg-surface-primary absolute right-1 bottom-1"
          onClick={(e) => {
            e?.stopPropagation();
            e?.preventDefault();
            setIsExpanded(true);
          }}
        />
      </div>
      <Modal
        isOpen={isExpanded}
        onClose={() => setIsExpanded(false)}
        ariaLabel={alt}
        className="max-h-[90vh] min-h-0 max-w-[90vw] overflow-y-auto"
      >
        <img
          src={src}
          alt={alt}
          className="note-drawable note-drawable-expanded w-full"
        />
      </Modal>
    </>
  );
}

export default NoteDrawable;
